const live = (rows) => (rows || []).filter((row) => row?.status !== "CANCELLED");

const rowIdsWithStatus = (entries, status) =>
  (entries || [])
    .flatMap((entry) => live(entry?.rows).filter((row) => row.status === status))
    .map((row) => row.id);

export const approvableRowIds = (entries) => rowIdsWithStatus(entries, "ACCRUED");

export const payableRowIds = (entries) => rowIdsWithStatus(entries, "APPROVED");

/**
 * An entry is paid whole or not at all, so the reason it cannot be paid belongs to the entry and
 * not to any one row. The server enforces the same three rules.
 */
export const entryBlockedReason = (entry) => {
  const rows = live(entry?.rows);
  if (rows.length === 0) return "This entry was cancelled.";
  if (rows.every((row) => row.status === "PAID")) return "Already paid.";
  if (rows.some((row) => row.status === "ACCRUED")) {
    return "A correction on this entry is waiting for approval. Approve it first, then pay the entry.";
  }
  if (rows.some((row) => row.status === "PAID")) {
    return "Part of this entry is already paid. Reverse that payout to settle it again.";
  }
  if ((Number(entry?.net) || 0) <= 0) return "Nothing to pay: this entry nets to zero or less.";
  return null;
};

export const canApproveEntry = (entry) => live(entry?.rows).some((row) => row.status === "ACCRUED");

export const selectionSummary = (entries) => {
  const list = entries || [];
  const net = list.reduce((total, entry) => total + (Number(entry?.net) || 0), 0);
  return {
    count: list.length,
    net,
    canApprove: list.length > 0 && list.every(canApproveEntry),
    canPay: list.length > 0 && list.every((entry) => entryBlockedReason(entry) === null),
  };
};

export const payoutBlockedReason = (entries) => {
  const list = entries || [];
  if (list.length === 0) return "Select an entry to pay.";
  const blocked = list.map(entryBlockedReason).find(Boolean);
  return blocked || null;
};

/**
 * Money by where it stands, summed row by row so a correction counts where it actually sits:
 * a pending correction is still awaiting approval even on an entry that was approved before.
 * Cancelled rows are left out.
 */
export const commissionTotals = (rows) => {
  const totals = { awaiting: 0, approved: 0, paid: 0, total: 0, entriesAwaiting: 0 };
  const awaitingEntries = new Set();
  live(rows).forEach((row) => {
    const amount = Number(row.amount) || 0;
    if (row.status === "ACCRUED") {
      totals.awaiting += amount;
      awaitingEntries.add(row.entryId ?? row.id);
    } else if (row.status === "APPROVED") {
      totals.approved += amount;
    } else if (row.status === "PAID") {
      totals.paid += amount;
    }
  });
  totals.total = totals.awaiting + totals.approved + totals.paid;
  totals.entriesAwaiting = awaitingEntries.size;
  return totals;
};
